/**
 * Share codes — a time-limited door onto one work, from the issuer's side.
 *
 * The agent rail's "Share Codes / Agree a Consignment", built. Its page said what
 * had to exist first — "a grant shape of its own that says who may re-share
 * and for how long" — and 20260910123000 is that shape: a redemption is a
 * viewer grant, so nobody re-shares; the code expires, and the issuer ending
 * it revokes every grant it made.
 *
 * "Agree a Consignment" is the second half of the label, and it IS this screen
 * — the lower half of it. An agent cannot sell; what they may do at editor, AND
 * ONLY IF THEIR CLIENT GRANTED IT, is agree a consignment of the client's work
 * to a gallery, and `ConsignPanel` mounted under a work picker is that door.
 * The gallery then sells, and the sale reaches the client's record.
 *
 * It read "Initiate a Sale" until OND-82, which is the ticket that renamed it:
 * the old half named the gallery's act, not the agent's.
 *
 * AND THE COLLECTOR RAIL'S "Share Codes", which is the first half only and came
 * later: the registry had answered for a work's owner since the day the feature
 * shipped, and the owner had no screen. That arm is words, not SQL — see
 * `ShareCodesCopy` and `OWNER_SHARE_CODES_COPY` below.
 */

/**
 * The words this screen draws from, in whichever voice the reader has.
 *
 * ONE SCREEN, TWO ISSUERS. `ondo.my_share_codes()` answers for an agent and
 * for a work's OWNER alike — its owner branch is `s.work_id in (select
 * work_id from ondo.owned_work_ids())`, and decision 6 of 20260910123000
 * argues for it deliberately — so giving the owner this report needed no SQL,
 * no route and no second screen. It needed the copy said in their own voice,
 * which is what this interface makes possible: `SHARE_CODES_COPY` is the
 * agent's and `OWNER_SHARE_CODES_COPY` below is the owner's.
 *
 * `sale` is nullable, and it is the interesting field. The agent's second half
 * is "Agree a Consignment", which for them means placing a client's work with
 * a gallery that can sell it. An owner selling their own work has no door ON
 * THIS SCREEN, so the panel is absent for the owner rather than reworded into
 * a promise.
 *
 * IT USED TO SAY "NO DOOR AT ALL — `Capabilities.transferWork` stays false for
 * them and says why", and OND-173 made all of that untrue in two steps. The
 * act landed first (`ondo.effect_owner_sale()`, `POST /v1/works/:id/sale`,
 * and the capability answers TRUE for an owner now), and then the screen:
 * `sell/OwnerSalePanel.tsx`, on the WORK's own record beside the consignment
 * panel. So this panel is still absent, but for a different and better reason
 * than before — a sale is about one work and this screen is a ledger of codes
 * across all of them, which is the same reason the rail names no destination
 * for it. `notHere` below points at the door.
 *
 * Everything from `groups` down to `emptyDetail` is VOCABULARY, not voice:
 * 'expires', 'issued by', 'Redeemed by' read the same whoever issued the
 * code, and the owner's object inherits them by spreading the agent's. A
 * string only belongs in an override if the two voices genuinely differ on it.
 */
/**
 * How many works the issue form's `<select>` offers.
 *
 * A hundred because `GET /v1/works/publishable` clamps `limit` there, so asking
 * for more would be silently trimmed and the number would be a lie. See
 * `readOwnedIssuableWorks`, which says what an owner of more than a hundred
 * works needs instead.
 */
export const PICKER_LIMIT = 100;

export interface ShareCodesCopy {
  loading: string;
  errorTitle: string;
  lead: string;
  leadDetail: string;
  issue: {
    title: string;
    work: string;
    workPlaceholder: string;
    days: string;
    daysNote: string;
    intendedFor: string;
    intendedForPlaceholder: string;
    note: string;
    notePlaceholder: string;
    submit: string;
    issuing: string;
    issued: string;
    copy: string;
    copied: string;
    noWorks: string;
  };
  groups: { live: string; ended: string };
  expires: string;
  expired: string;
  ended: string;
  issuedBy: string;
  forFirm: string;
  intendedFor: string;
  nobodyYet: string;
  redeemedBy: string;
  end: string;
  endConfirm: string;
  endCancel: string;
  endNote: string;
  empty: string;
  emptyDetail: string;
  /** Null where the reader has no sale door of any kind. See above. */
  sale: {
    title: string;
    lead: string;
    detail: string;
    work: string;
    workPlaceholder: string;
  } | null;
  notHere: { title: string; points: readonly string[] };
  errors: { refused: string; throttled: string };
}

export const SHARE_CODES_COPY = {
  loading: "Reading your codes…",
  errorTitle: "Can’t load your share codes",

  lead: "A code is one work, shown to one person, for a while.",
  leadDetail:
    "Whoever redeems it can read the work’s record — the photographs and the chain of title — and nothing more: they cannot share it on, sell it, or change it. The code expires on the date you set, and ending it early closes the door for everyone who came through. The registry records who did.",

  issue: {
    title: "Issue a code",
    work: "Work",
    workPlaceholder: "Choose a work you represent",
    days: "Open for",
    daysNote: "Between one and thirty days.",
    intendedFor: "For",
    intendedForPlaceholder: "Who it is meant for — a name, a firm, a fair (optional)",
    note: "Note",
    notePlaceholder: "Anything you want on the record beside it (optional)",
    submit: "Issue code",
    issuing: "Issuing…",
    /** Shown once, beside the new code. It can be read again from the list. */
    issued: "Issued. Give this code to the person it is for:",
    copy: "Copy",
    copied: "Copied",
    noWorks: "You represent no work at editor level yet, so there is nothing to issue a code for.",
  },

  groups: {
    live: "Open",
    ended: "Ended or expired",
  },

  /** 'expires 21 Sep 2026' / 'expired 21 Sep 2026' / 'ended 14 Sep 2026'. */
  expires: "expires",
  expired: "expired",
  ended: "ended",
  issuedBy: "issued by",
  forFirm: "for",
  intendedFor: "for",
  nobodyYet: "Nobody has come through yet.",
  redeemedBy: "Redeemed by",

  end: "End this code",
  endConfirm: "Yes, end it",
  endCancel: "Keep it open",
  endNote:
    "Ends the code and revokes the read it gave to everyone who redeemed it. The code stays on the record, marked ended.",

  empty: "You have issued no codes.",
  emptyDetail:
    "Issue one from the form above to show a work you represent to somebody — a buyer, an adviser, a fair — without giving them anything else.",

  /**
   * The second half of the rail's label.
   *
   * ── IT WAS CALLED "INITIATE A SALE" AND THAT WAS WRONG, OND-82 ──
   *
   * "The interface currently calls it 'initiate a sale', which can make the
   * flow confusing. This action agrees a CONSIGNMENT: the gallery takes the
   * work to sell it. It is not itself a sale to a buyer or a transfer of
   * ownership."
   *
   * The old name named a thing this door cannot do. An agent cannot sell —
   * `Capabilities.transferWork` is false for them and
   * `ondo.sellable_work_ids()` is the gallery's set — so a screen headed
   * "Initiate a sale" promised an act the database refuses, and the sentence
   * underneath it had to spend its first clause taking the promise back.
   *
   * Now the heading says what the button does and the lead says what it is not.
   */
  sale: {
    title: "Agree a consignment",
    lead: "The gallery takes the work to sell it. This is not a sale and it does not move ownership.",
    detail:
      "Choose a work you represent and offer it to a gallery on terms. The gallery accepts or declines; once they accept, it is in their inventory, they record any sale, and the transfer reaches your client’s record. Agreeing a consignment needs a seat at editor level or above, as issuing a code does — and your client must have included it in the scope they granted you.",
    work: "Work",
    workPlaceholder: "Choose a work you represent",
  },

  /** What is not on this screen, said plainly rather than left hanging. */
  notHere: {
    title: "Not on this screen",
    points: [
      "Selling. The sale itself is the gallery’s act, recorded from their inventory once they hold the work.",
      "Codes issued by the owner. A work’s owner may issue a code from the same door; when they do, it appears here beside yours.",
    ],
  },

  errors: {
    refused: "That work is not one you may issue a code for.",
    throttled: "Too many guesses this hour. Try again later.",
  },
} as const satisfies ShareCodesCopy;

/**
 * The same screen in the owner's voice, on the collector's rail.
 *
 * FIVE STRINGS, not four. Four of them say "represent", which is what an agent
 * does to somebody else's work and not what an owner does to their own:
 * `issue.workPlaceholder`, `issue.noWorks`, `emptyDetail`, and everything in
 * `sale` — which is gone entirely rather than reworded. The fifth is the
 * second `notHere` point, which told an agent that the owner's codes appear
 * beside theirs; on the owner's own screen that sentence is backwards, so the
 * fact it carries is turned round and moved into `leadDetail`, where it is
 * true and always visible rather than filed under what is absent.
 *
 * Nothing else is overridden, and that is deliberate: see `ShareCodesCopy`.
 */
export const OWNER_SHARE_CODES_COPY: ShareCodesCopy = {
  ...SHARE_CODES_COPY,

  leadDetail:
    "Whoever redeems it can read the work’s record — the photographs and the chain of title — and nothing more: they cannot share it on, sell it, or change it. The code expires on the date you set, and ending it early closes the door for everyone who came through. The registry records who did. A code an agent you appointed has issued on one of your works is listed here beside your own, and you can end that one too.",

  issue: {
    ...SHARE_CODES_COPY.issue,
    workPlaceholder: "Choose one of your works",
    noWorks: "You own no work yet, so there is nothing to issue a code for.",
  },

  emptyDetail:
    "Issue one from the form above to show a work of yours to somebody — a buyer, an adviser, a fair — without giving them anything else.",

  // An owner has no direct-sale door ON THIS SCREEN, and since OND-173 that is
  // the whole of the claim: the act exists (`ondo.effect_owner_sale()`,
  // `POST /v1/works/:id/sale`) and so does the screen for it — it is a panel on
  // the WORK's own record, beside the consignment panel, because a sale is
  // about one work and this screen is a ledger of codes. So the panel here is
  // absent and `notHere` below now POINTS at the door rather than saying there
  // is none, which is the half that went false the moment the act landed.
  sale: null,

  notHere: {
    title: SHARE_CODES_COPY.notHere.title,
    points: [
      // THIS SENTENCE USED TO SAY there was no door for selling your own work
      // directly and that "a sale is recorded by a gallery from its inventory,
      // once you have consigned the work to one". The second half is now false
      // and the first is only true of this screen, so it names where the door
      // is. It promises nothing about being told or chased: a buyer on ONDO is
      // offered title and may never answer, and nothing expires.
      "Selling. Recording a sale of your own work is on the work’s own record — open the work and use “Sell this work”. A work you have consigned to a gallery is theirs to sell while that consignment stands.",
      "Lending and consigning. A code is a read of the record and nothing else — where the work physically goes is on Loans, Consign & Agents.",
    ],
  },
};

/** The collector's side: a box to type a code into. */
export const REDEEM_COPY = {
  title: "Have a share code?",
  lead: "Somebody showing you a work will have given you one. It opens that work’s record to you, and nothing else.",
  label: "Code",
  placeholder: "OND-S-XXXX-XXXX",
  submit: "Open the work",
  opening: "Opening…",
  opened: "Opened. The work is in what you can read now.",
  view: "View the work →",
  refused: "That code cannot be redeemed.",
  throttled: "Too many guesses this hour. Try again later.",
} as const;
